import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../CSS/HRLeaveManagement.css";
import {
  fetchPendingLeaves,
  fetchAllLeaves,
  fetchLeaveSummary,
  deptHeadApproval,
  hrApproval,
} from "../features/leave/leaveThunks";
import {
  clearUpdateSuccess,
} from "../features/leave/leaveSlice";
import Sidebar from './Sidebar';

const LEAVE_TYPE_LABELS = {
  CASUAL:  'Casual Leave',
  MEDICAL: 'Medical Leave',
  EARNED:  'Earned Leave',
};

const PLANNING_LABELS = {
  PLANNED:     'Planned',
  UNPLANNED:   'Unplanned',
  LAST_MINUTE: 'Last Minute',
};

const DURATION_LABELS = {
  FULL_DAY: 'Full Day',
  HALF_DAY: 'Half Day',
};

const TableSkeleton = () => {
  return (
    <div className="hr-table-skeleton">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="hr-skeleton-row">
          <div className="hr-skeleton-cell hr-skeleton-avatar"></div>
          <div className="hr-skeleton-cell hr-skeleton-text" style={{ width: '120px' }}></div>
          <div className="hr-skeleton-cell hr-skeleton-text" style={{ width: '100px' }}></div>
          <div className="hr-skeleton-cell hr-skeleton-text" style={{ width: '180px' }}></div>
          <div className="hr-skeleton-cell hr-skeleton-text" style={{ width: '60px' }}></div>
          <div className="hr-skeleton-cell hr-skeleton-text" style={{ width: '100px' }}></div>
          <div className="hr-skeleton-cell hr-skeleton-badge"></div>
          <div className="hr-skeleton-cell hr-skeleton-actions">
            <div className="hr-skeleton-action-btn"></div>
            <div className="hr-skeleton-action-btn"></div>
            <div className="hr-skeleton-action-btn"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const HRLeaveManagement = () => {
  const dispatch = useDispatch();

  const {
    allLeaves,
    allLeavesLoading,
    pendingLeaves,
    pendingLeavesLoading,
    pendingCount,
    approvalLevel,
    leaveSummary,
    isUpdating,
    updateSuccess,
    updateError,
    updateMessage,
  } = useSelector((state) => state.leave);

  const [filter, setFilter] = useState("pending");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [actionModal, setActionModal] = useState({ isOpen: false, action: null });
  const [remark, setRemark] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [planningFilter, setPlanningFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchPendingLeaves());
    dispatch(fetchAllLeaves());
    dispatch(fetchLeaveSummary());
  }, [dispatch, filter]);

  useEffect(() => {
    if (updateSuccess) {
      alert(updateMessage || "Leave request updated successfully!");
      dispatch(clearUpdateSuccess());
      setActionModal({ isOpen: false, action: null });
      setSelectedLeave(null);
      setRemark("");
      dispatch(fetchPendingLeaves());
      dispatch(fetchAllLeaves());
      dispatch(fetchLeaveSummary());
    }
  }, [updateSuccess, updateMessage, dispatch, filter]);

  useEffect(() => {
    if (updateError) {
      alert(`Failed: ${updateError}`);
      dispatch(clearUpdateSuccess());
    }
  }, [updateError, dispatch]);

  const getLeaveList = () => (filter === "pending" ? pendingLeaves : allLeaves);

  const getFilteredRequests = () => {
    let list = getLeaveList();

    // Filter by tab — use pre-computed flags so this works with both raw and
    // friendly status shapes (pending tab data has raw; all tab data has friendly)
    if (filter === "approved") {
      list = list.filter((l) => l.isApproved);
    } else if (filter === "rejected") {
      list = list.filter((l) => l.isRejected);
    } else if (filter === "pending") {
      list = list.filter((l) => l.isPending);
    }
    // filter === "all" → no status filter

    if (departmentFilter !== "all") {
      list = list.filter((l) => l.department === departmentFilter);
    }
    if (planningFilter !== "all") {
      list = list.filter((l) => l.planningType === planningFilter);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(
        (l) =>
          (l.employeeCode && l.employeeCode.toLowerCase().includes(s)) ||
          (l.employeeName && l.employeeName.toLowerCase().includes(s))
      );
    }
    const unique = {};
    for (const leave of list) {
      if (!unique[leave.id] || new Date(leave.appliedDate) > new Date(unique[leave.id].appliedDate)) {
        unique[leave.id] = leave;
      }
    }
    return Object.values(unique);
  };

  const departments = ["all", ...new Set(getLeaveList().map((l) => l.department).filter(Boolean))];

  const handleApproveReject = (leave, intent) => {
    let action;
    // Use pre-computed flags (requiresDeptHead / requiresHR) so this works
    // regardless of whether leave.status is a raw or friendly value.
    if (leave.requiresDeptHead) {
      action = intent === "approve" ? "dept-approve" : "dept-reject";
    } else if (leave.requiresHR) {
      action = intent === "approve" ? "hr-approve" : "hr-reject";
    } else if (approvalLevel === "ADMIN" && leave.isPending) {
      // ADMIN acting on a leave where rawStatus may not be set: try both endpoints.
      // Default to dept-head stage (backend will validate and return error if wrong).
      action = intent === "approve" ? "dept-approve" : "dept-reject";
    } else {
      return; // Leave is not in an actionable state
    }
    setSelectedLeave(leave);
    setActionModal({ isOpen: true, action });
    setRemark("");
  };

  const submitApprovalAction = async () => {
    if (!remark.trim()) {
      alert("Please provide a comment");
      return;
    }
    const { action } = actionModal;
    const leaveId = selectedLeave.id;
    try {
      if (action === "dept-approve") {
        await dispatch(deptHeadApproval({ leaveId, action: "APPROVE", remark })).unwrap();
      } else if (action === "dept-reject") {
        await dispatch(deptHeadApproval({ leaveId, action: "REJECT", remark })).unwrap();
      } else if (action === "hr-approve") {
        await dispatch(hrApproval({ leaveId, action: "APPROVE", remark })).unwrap();
      } else if (action === "hr-reject") {
        await dispatch(hrApproval({ leaveId, action: "REJECT", remark })).unwrap();
      }
    } catch (err) {
      console.error("Error updating leave:", err);
    }
  };

  const getStatusInfo = (leave) => {
    // Prefer rawStatus for granular detail; fall back to friendly status
    const raw = leave.rawStatus;
    if (raw) {
      switch (raw) {
        case 'APPROVED':
          return { label: 'Approved', className: 'approved' };
        case 'REJECTED_BY_DEPT_HEAD':
          return { label: 'Rejected by Dept Head', className: 'rejected' };
        case 'REJECTED_BY_HR':
          return { label: 'Rejected by HR', className: 'rejected' };
        case 'PENDING_HR':
          return { label: 'Pending HR', className: 'pending' };
        case 'PENDING_DEPT_HEAD':
        default:
          return { label: 'Pending Dept Head', className: 'pending' };
      }
    }
    // Friendly-only fallback (rawStatus not available)
    switch (leave.status) {
      case 'APPROVED': return { label: 'Approved',  className: 'approved' };
      case 'REJECTED': return { label: 'Rejected',  className: 'rejected' };
      default:         return { label: 'Pending',   className: 'pending' };
    }
  };

  const StatusIcon = ({ className }) => {
    if (className === "approved") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 6L9 17l-5-5"></path>
        </svg>
      );
    }
    if (className === "rejected") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      );
    }
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    );
  };

  const canApproveReject = (leave) => {
    // ADMIN sees all pending leaves
    if (approvalLevel === "ADMIN") return leave.isPending;
    // Dept head can act when leave is at dept-head stage
    if (approvalLevel === "DEPARTMENT_HEAD") return leave.requiresDeptHead;
    // HR can act when dept head has already approved (PENDING_HR)
    if (approvalLevel === "HR") return leave.requiresHR;
    return false;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const modalTitle = () => {
    const { action } = actionModal;
    if (action?.startsWith("dept")) {
      return action.includes("approve") ? "Department Head Approval" : "Department Head Rejection";
    }
    return action?.includes("approve") ? "HR Approval" : "HR Rejection";
  };

  const filteredRequests = getFilteredRequests();
  const isLoading = filter === "pending" ? pendingLeavesLoading : allLeavesLoading;

  return (
    <div className="index-box">
      <Sidebar />
      <div className="hr-leave-container">
        <div className="hr-leave-header">
          <div className="hr-leave-header-content">
            <div className="hr-leave-title-row">
              <h2 className="hr-leave-title">Leave Management</h2>
              {approvalLevel && (
                <div className="hr-role-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  {approvalLevel === 'ADMIN' ? 'Administrator' : 
                   approvalLevel === 'DEPARTMENT_HEAD' ? 'Department Head' : 
                   approvalLevel === 'HR' ? 'HR Manager' : approvalLevel}
                </div>
              )}
            </div>
            <p className="hr-leave-subtitle">
              Review and manage employee leave requests
            </p>
          </div>
          <div className="hr-stats-cards">
            <div className="hr-stat-card hr-stat-pending">
              <div className="hr-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className="hr-stat-content">
                <div className="hr-stat-value">{leaveSummary.pending || 0}</div>
                <div className="hr-stat-label">Pending</div>
              </div>
            </div>
            <div className="hr-stat-card hr-stat-approved">
              <div className="hr-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
              </div>
              <div className="hr-stat-content">
                <div className="hr-stat-value">{leaveSummary.approved || 0}</div>
                <div className="hr-stat-label">Approved</div>
              </div>
            </div>
            <div className="hr-stat-card hr-stat-rejected">
              <div className="hr-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </div>
              <div className="hr-stat-content">
                <div className="hr-stat-value">{leaveSummary.rejected || 0}</div>
                <div className="hr-stat-label">Rejected</div>
              </div>
            </div>
          </div>
        </div>

        <div className="hr-filters-section">
          <div className="hr-search-wrapper">
            <div className="hr-search-input-group">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by employee name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="hr-search-input"
              />
            </div>
            <div className="hr-filter-dropdowns-group">
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="hr-filter-dropdown"
              >
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept === "all" ? "All Departments" : dept}
                  </option>
                ))}
              </select>
              <select
                value={planningFilter}
                onChange={(e) => setPlanningFilter(e.target.value)}
                className="hr-filter-dropdown"
              >
                <option value="all">All Planning Types</option>
                <option value="PLANNED">Planned</option>
                <option value="UNPLANNED">Unplanned</option>
                <option value="LAST_MINUTE">Last Minute</option>
              </select>
            </div>
          </div>

          <div className="hr-filter-tabs-wrapper">
            {["pending", "approved", "rejected", "all"].map((key) => (
              <button
                key={key}
                className={`hr-filter-tab ${filter === key ? "hr-filter-tab-active" : ""}`}
                onClick={() => setFilter(key)}
              >
                <span className="hr-filter-tab-label">
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
                <span className="hr-filter-tab-count">
                  ({key === "all" ? leaveSummary.totalLeaves : leaveSummary[key] || 0})
                </span>
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="hr-table-wrapper">
            <TableSkeleton />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="hr-empty-state">
            <div className="hr-empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <p className="hr-empty-text">No leave requests found</p>
          </div>
        ) : (
          <div className="hr-table-wrapper">
            <table className="hr-leave-table">
              <thead className="hr-table-head">
                <tr>
                  <th className="hr-table-header">Employee</th>
                  <th className="hr-table-header">Department</th>
                  <th className="hr-table-header">Leave Type</th>
                  <th className="hr-table-header">Dates</th>
                  <th className="hr-table-header">Days</th>
                  <th className="hr-table-header">Applied</th>
                  <th className="hr-table-header">Status</th>
                  <th className="hr-table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="hr-table-body">
                {filteredRequests.map((leave) => {
                  const status = getStatusInfo(leave);
                  const initials = leave.employeeName
                    ? leave.employeeName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()
                    : "??";

                  return (
                    <tr key={leave.id} className="hr-table-row">
                      <td className="hr-table-cell">
                        <div className="hr-employee-info">
                          <div className="hr-employee-avatar">{initials}</div>
                          <div className="hr-employee-details">
                            <div className="hr-employee-name">{leave.employeeName || "N/A"}</div>
                            <div className="hr-employee-meta">
                              <span className="hr-employee-code">{leave.employeeCode || "N/A"}</span>
                              {leave.designation && (
                                <>
                                  <span className="hr-meta-separator">•</span>
                                  <span className="hr-employee-designation">{leave.designation}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hr-table-cell">
                        <span className="hr-dept-badge">{leave.department || "N/A"}</span>
                      </td>
                      <td className="hr-table-cell">
                        <div className="hr-leave-type-info">
                          <span className="hr-leave-type-text">
                            {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                          </span>
                          <span className={`hr-planning-badge hr-planning-${(leave.planningType || "").toLowerCase()}`}>
                            {PLANNING_LABELS[leave.planningType] || leave.planningType || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="hr-table-cell">
                        <div className="hr-date-range">
                          <span className="hr-date-text">{formatDate(leave.fromDate)}</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                          <span className="hr-date-text">{formatDate(leave.toDate)}</span>
                        </div>
                      </td>
                      <td className="hr-table-cell">
                        <span className="hr-days-badge">{leave.totalDays}</span>
                      </td>
                      <td className="hr-table-cell">
                        <span className="hr-applied-date">{formatDate(leave.appliedDate)}</span>
                      </td>
                      <td className="hr-table-cell">
                        <span className={`hr-status-badge hr-status-${status.className}`}>
                          <StatusIcon className={status.className} />
                          {status.label}
                        </span>
                      </td>
                      <td className="hr-table-cell">
                        <div className="hr-actions-group">
                          <button
                            className="hr-action-btn hr-action-view"
                            onClick={() => setSelectedLeave(leave)}
                            title="View Details"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          {canApproveReject(leave) && (
                            <>
                              <button
                                className="hr-action-btn hr-action-approve"
                                onClick={() => handleApproveReject(leave, "approve")}
                                title="Approve"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M20 6L9 17l-5-5"></path>
                                </svg>
                              </button>
                              <button
                                className="hr-action-btn hr-action-reject"
                                onClick={() => handleApproveReject(leave, "reject")}
                                title="Reject"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedLeave && !actionModal.isOpen && (
          <div className="hr-modal-overlay" onClick={() => setSelectedLeave(null)}>
            <div className="hr-modal" onClick={(e) => e.stopPropagation()}>
              <div className="hr-modal-header">
                <h3 className="hr-modal-title">Leave Request Details</h3>
                <button className="hr-modal-close" onClick={() => setSelectedLeave(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="hr-modal-body">
                <div className="hr-modal-section">
                  <h4 className="hr-modal-section-title">Leave Information</h4>
                  <div className="hr-modal-grid">
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Employee:</label>
                      <span className="hr-modal-value">{selectedLeave.employeeName || "N/A"}</span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Code:</label>
                      <span className="hr-modal-value">{selectedLeave.employeeCode || "N/A"}</span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Department:</label>
                      <span className="hr-modal-value">{selectedLeave.department || "N/A"}</span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Leave Type:</label>
                      <span className="hr-modal-value">
                        {LEAVE_TYPE_LABELS[selectedLeave.leaveType] || selectedLeave.leaveType}
                      </span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Planning:</label>
                      <span className={`hr-planning-badge hr-planning-${(selectedLeave.planningType || "").toLowerCase()}`}>
                        {PLANNING_LABELS[selectedLeave.planningType] || selectedLeave.planningType}
                      </span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Duration:</label>
                      <span className="hr-modal-value">
                        {DURATION_LABELS[selectedLeave.duration] || selectedLeave.duration}
                      </span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">From:</label>
                      <span className="hr-modal-value">{formatDate(selectedLeave.fromDate)}</span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">To:</label>
                      <span className="hr-modal-value">{formatDate(selectedLeave.toDate)}</span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Days:</label>
                      <span className="hr-modal-value">{selectedLeave.totalDays}</span>
                    </div>
                    <div className="hr-modal-item">
                      <label className="hr-modal-label">Applied:</label>
                      <span className="hr-modal-value">{formatDate(selectedLeave.appliedDate)}</span>
                    </div>
                    <div className="hr-modal-item hr-modal-item-full">
                      <label className="hr-modal-label">Reason:</label>
                      <span className="hr-modal-value">{selectedLeave.reason || "N/A"}</span>
                    </div>
                  </div>
                </div>

                <div className="hr-modal-section">
                  <h4 className="hr-modal-section-title">Approval Status</h4>
                  <div className="hr-approval-timeline">
                    <div className={`hr-timeline-step ${
                      selectedLeave.deptHeadApproved ? 'hr-timeline-completed' :
                      selectedLeave.rejectedByDeptHead ? 'hr-timeline-rejected' : 'hr-timeline-pending'
                    }`}>
                      <div className="hr-timeline-indicator">
                        {selectedLeave.deptHeadApproved ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        ) : selectedLeave.rejectedByDeptHead ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        ) : '1'}
                      </div>
                      <div className="hr-timeline-content">
                        <div className="hr-timeline-title">Department Head</div>
                        <div className="hr-timeline-status">
                          {selectedLeave.deptHeadApproved ? 'Approved' :
                           selectedLeave.rejectedByDeptHead ? 'Rejected' : 'Pending'}
                        </div>
                        {selectedLeave.deptHeadRemark && (
                          <div className="hr-timeline-comment">"{selectedLeave.deptHeadRemark}"</div>
                        )}
                        {selectedLeave.deptHeadApprovedAt && (
                          <div className="hr-timeline-date">{formatDate(selectedLeave.deptHeadApprovedAt)}</div>
                        )}
                      </div>
                    </div>

                    <div className="hr-timeline-connector">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div>

                    <div className={`hr-timeline-step ${
                      selectedLeave.hrApproved ? 'hr-timeline-completed' :
                      selectedLeave.rejectedByHR ? 'hr-timeline-rejected' :
                      selectedLeave.deptHeadApproved ? 'hr-timeline-pending' : 'hr-timeline-disabled'
                    }`}>
                      <div className="hr-timeline-indicator">
                        {selectedLeave.hrApproved ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        ) : selectedLeave.rejectedByHR ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        ) : '2'}
                      </div>
                      <div className="hr-timeline-content">
                        <div className="hr-timeline-title">HR Department</div>
                        <div className="hr-timeline-status">
                          {selectedLeave.hrApproved ? 'Approved' :
                           selectedLeave.rejectedByHR ? 'Rejected' :
                           selectedLeave.deptHeadApproved ? 'Pending' : 'Awaiting Dept Head'}
                        </div>
                        {selectedLeave.hrRemark && (
                          <div className="hr-timeline-comment">"{selectedLeave.hrRemark}"</div>
                        )}
                        {selectedLeave.hrApprovedAt && (
                          <div className="hr-timeline-date">{formatDate(selectedLeave.hrApprovedAt)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hr-modal-footer">
                <button className="hr-modal-btn-cancel" onClick={() => setSelectedLeave(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {actionModal.isOpen && (
          <div className="hr-modal-overlay" onClick={() => setActionModal({ isOpen: false, action: null })}>
            <div className="hr-modal" onClick={(e) => e.stopPropagation()}>
              <div className="hr-modal-header">
                <h3 className="hr-modal-title">{modalTitle()}</h3>
                <button className="hr-modal-close" onClick={() => setActionModal({ isOpen: false, action: null })}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="hr-modal-body">
                <div className="hr-action-employee-card">
                  <div className="hr-action-employee-name">{selectedLeave?.employeeName || "N/A"}</div>
                  <div className="hr-action-employee-code">{selectedLeave?.employeeCode || "N/A"}</div>
                </div>

                <div className="hr-action-leave-card">
                  <div className="hr-action-leave-row">
                    <span className="hr-action-leave-label">Leave Type:</span>
                    <span className="hr-action-leave-value">
                      {LEAVE_TYPE_LABELS[selectedLeave?.leaveType] || selectedLeave?.leaveType}
                    </span>
                  </div>
                  <div className="hr-action-leave-row">
                    <span className="hr-action-leave-label">Planning:</span>
                    <span className="hr-action-leave-value">
                      {PLANNING_LABELS[selectedLeave?.planningType] || selectedLeave?.planningType}
                    </span>
                  </div>
                  <div className="hr-action-leave-row">
                    <span className="hr-action-leave-label">Duration:</span>
                    <span className="hr-action-leave-value">
                      {DURATION_LABELS[selectedLeave?.duration] || selectedLeave?.duration}
                    </span>
                  </div>
                  <div className="hr-action-leave-row">
                    <span className="hr-action-leave-label">Dates:</span>
                    <span className="hr-action-leave-value">
                      {formatDate(selectedLeave?.fromDate)} → {formatDate(selectedLeave?.toDate)} ({selectedLeave?.totalDays} days)
                    </span>
                  </div>
                  <div className="hr-action-leave-row hr-action-leave-row-full">
                    <span className="hr-action-leave-label">Reason:</span>
                    <span className="hr-action-leave-value">{selectedLeave?.reason || "N/A"}</span>
                  </div>
                </div>

                <div className="hr-action-form-group">
                  <label className="hr-action-form-label">
                    {actionModal.action?.startsWith("dept") ? "Department Head Comment" : "HR Comment"}
                    <span className="hr-action-form-required">*</span>
                  </label>
                  <textarea
                    rows="5"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder={`Provide reason for ${actionModal.action?.includes("approve") ? "approval" : "rejection"}...`}
                    className="hr-action-form-textarea"
                  ></textarea>
                </div>
              </div>

              <div className="hr-modal-footer">
                <button 
                  className="hr-modal-btn-cancel" 
                  onClick={() => setActionModal({ isOpen: false, action: null })}
                >
                  Cancel
                </button>
                <button
                  className={`hr-modal-btn-submit ${actionModal.action?.includes("approve") ? "hr-modal-btn-approve" : "hr-modal-btn-reject"}`}
                  onClick={submitApprovalAction}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <span className="hr-submit-spinner"></span>
                      Processing...
                    </>
                  ) : actionModal.action?.includes("approve") ? (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5"></path>
                      </svg>
                      Approve Leave
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                      Reject Leave
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HRLeaveManagement;